import {
  index,
  integer,
  primaryKey,
  real,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import { user } from "./auth";
import { venue } from "./venue";

// -----------------------------
// Enums / Types
// -----------------------------
export const LIKE_TARGET = ["activity", "profile"] as const;
export type LikeTarget = (typeof LIKE_TARGET)[number];

export const REQUEST_STATUS = [
  "pending",
  "accepted",
  "declined",
  "expired",
  "canceled",
] as const;
export type RequestStatus = (typeof REQUEST_STATUS)[number];

export const CONNECTION_STATUS = ["active", "blocked", "ended"] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUS)[number];

export const SOCIAL_GRAPH_SCOPE_TYPE = [
  "user",
  "hiive",
  "occurrence",
  "venue",
] as const;
export type SocialGraphScopeType = (typeof SOCIAL_GRAPH_SCOPE_TYPE)[number];

// -----------------------------
// Connection Requests (the Hinge-style like)
// -----------------------------
export const connectionRequest = sqliteTable(
  "connection_request",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    fromUserId: text("from_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    toUserId: text("to_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    targetType: text("target_type").notNull().$type<LikeTarget>(), // 'profile' | 'activity'
    targetId: text("target_id"), // NOT NULL iff targetType='activity'

    message: text("message"), // optional comment on the like

    status: text("status").notNull().$type<RequestStatus>().default("pending"),

    expiresAt: integer("expires_at", { mode: "timestamp" }),
    seenAt: integer("seen_at", { mode: "timestamp" }),

    ...timestamps,
  },
  (table) => [
    index("cr_inbox_to_pending").on(table.toUserId, table.status), // show likes you received
    index("cr_outbox_from_pending").on(table.fromUserId, table.status), // show likes you sent
    index("cr_by_target").on(table.targetType, table.targetId),

    // One PENDING per pair (clean & Hinge-like)
    uniqueIndex("cr_one_pending_pair").on(
      table.fromUserId,
      table.toUserId,
      table.status,
    ),
  ],
);

// -----------------------------
// Buddy Connections
// -----------------------------
export const buddyConnection = sqliteTable(
  "buddy_connection",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    userAId: text("user_a_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    userBId: text("user_b_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    status: text("status")
      .notNull()
      .$type<ConnectionStatus>()
      .default("active"),

    endedAt: integer("ended_at", { mode: "timestamp" }),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("conn_unique_pair").on(table.userAId, table.userBId),
    index("conn_by_user_a").on(table.userAId),
    index("conn_by_user_b").on(table.userBId),
  ],
);

// -----------------------------
// Blocklist (short-circuit everything)
// -----------------------------
export const buddyBlock = sqliteTable(
  "buddy_block",
  {
    blockerId: text("blocker_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    blockedId: text("blocked_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    reason: text("reason"),
    ...timestamps,
  },
  (table) => [
    primaryKey({
      name: "buddy_block_pk",
      columns: [table.blockerId, table.blockedId],
    }),
  ],
);

// -----------------------------
// Mutes (one-way, private, and non-destructive)
// -----------------------------
export const buddyMute = sqliteTable(
  "buddy_mute",
  {
    muterId: text("muter_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    mutedId: text("muted_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    mutePosts: integer("mute_posts", { mode: "boolean" })
      .notNull()
      .default(true),
    muteStories: integer("mute_stories", { mode: "boolean" })
      .notNull()
      .default(true),
    ...timestamps,
  },
  (table) => [
    primaryKey({
      name: "buddy_mute_pk",
      columns: [table.muterId, table.mutedId],
    }),
    index("buddy_mute_muter_idx").on(table.muterId),
  ],
);

// -----------------------------
// Legacy recommendation storage — retained during the Redis release.
// Runtime recommendation readers and writers use expiring Redis state. Keep
// these definitions until deployed and rollback artifacts both tolerate drops;
// see advisor-plans/redis-recommendation-release.md before removing them.
// -----------------------------
export const dailyPotentialBuddy = sqliteTable(
  "daily_potential_buddy",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    profileId: text("profile_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    activityId: text("activity_id").notNull().default("general"),

    expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
    ...timestamps,
  },
  (table) => [
    index("dpb_by_user").on(table.userId),
    index("dpb_by_expiry").on(table.expiresAt),
    uniqueIndex("dpb_unique_pair").on(
      table.userId,
      table.profileId,
      table.activityId,
    ),
  ],
);

export const potentialBuddyHistory = sqliteTable(
  "potential_buddy_history",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    profileId: text("profile_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    activityId: text("activity_id").notNull().default("general"),

    seenAt: integer("seen_at", { mode: "timestamp" }).notNull(),
    cycle: integer("cycle").notNull().default(0),
    ...timestamps,
  },
  (table) => [
    index("pbh_by_user").on(table.userId),
    index("pbh_by_user_cycle").on(table.userId, table.cycle),
    uniqueIndex("pbh_unique_pair").on(
      table.userId,
      table.profileId,
      table.activityId,
    ),
  ],
);

export const socialSuggestionCandidateCache = sqliteTable(
  "social_suggestion_candidate_cache",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    viewerId: text("viewer_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    candidateId: text("candidate_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    mutualCount: integer("mutual_count").notNull().default(0),
    sharedHiiveCount: integer("shared_hiive_count").notNull().default(0),
    sharedEventCount: integer("shared_event_count").notNull().default(0),
    sharedVenueCount: integer("shared_venue_count").notNull().default(0),
    contactMatchCount: integer("contact_match_count").notNull().default(0),

    socialScore: real("social_score").notNull().default(0),
    aiScore: real("ai_score").notNull().default(0),
    finalScore: real("final_score").notNull().default(0),

    reasonChips: text("reason_chips", { mode: "json" }).$type<string[]>(),
    scoreMeta: text("score_meta", { mode: "json" }).$type<
      Record<string, number>
    >(),
    suggestedVenueId: integer("suggested_venue_id").references(() => venue.id, {
      onDelete: "set null",
    }),

    computedAt: integer("computed_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),
    expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("ssc_unique_viewer_candidate").on(
      table.viewerId,
      table.candidateId,
    ),
    index("ssc_by_viewer_score").on(table.viewerId, table.finalScore),
    index("ssc_by_expiry").on(table.expiresAt),
  ],
);

export const socialSuggestionExposure = sqliteTable(
  "social_suggestion_exposure",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    viewerId: text("viewer_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    candidateId: text("candidate_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    impressionCount: integer("impression_count").notNull().default(0),
    profileOpenCount: integer("profile_open_count").notNull().default(0),
    connectClickCount: integer("connect_click_count").notNull().default(0),
    bookSlotClickCount: integer("book_slot_click_count").notNull().default(0),
    lastShownAt: integer("last_shown_at", { mode: "timestamp" }),
    nextEligibleAt: integer("next_eligible_at", { mode: "timestamp" }),
    lastSurface: text("last_surface"),
    ...timestamps,
  },
  (table) => [
    uniqueIndex("sse_unique_viewer_candidate").on(
      table.viewerId,
      table.candidateId,
    ),
    index("sse_by_viewer").on(table.viewerId),
    index("sse_by_next_eligible").on(table.nextEligibleAt),
  ],
);

export const socialGraphChangeToken = sqliteTable(
  "social_graph_change_token",
  {
    scopeType: text("scope_type").notNull().$type<SocialGraphScopeType>(),
    scopeId: text("scope_id").notNull(),
    version: integer("version").notNull().default(0),
    updatedAt: integer("updated_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    primaryKey({
      name: "sgct_pk",
      columns: [table.scopeType, table.scopeId],
    }),
    index("sgct_scope_lookup").on(table.scopeType, table.scopeId),
  ],
);

export const socialSuggestionViewerCacheState = sqliteTable(
  "social_suggestion_viewer_cache_state",
  {
    viewerId: text("viewer_id")
      .primaryKey()
      .references(() => user.id, { onDelete: "cascade" }),
    dependencyFingerprint: text("dependency_fingerprint").notNull(),
    computedAt: integer("computed_at", { mode: "timestamp" }).notNull(),
    expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
    lastEnqueuedFingerprint: text("last_enqueued_fingerprint"),
    lastEnqueuedAt: integer("last_enqueued_at", { mode: "timestamp" }),
    lastError: text("last_error"),
    updatedAt: integer("updated_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date()),
  },
  (table) => [
    index("ssvcs_expires").on(table.expiresAt),
    index("ssvcs_last_enqueued").on(table.lastEnqueuedAt),
  ],
);
